import React, { useState, useRef } from "react";
import { uploadApi } from "@/lib/api-client";
import { extractErrorMessage } from "@/lib/utils";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Camera,
  FileCheck2,
  AlertTriangle,
  UploadCloud,
  Trash2,
  ExternalLink,
  Loader2,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
} from "lucide-react";

export type QcMediaCategory = "SAMPLE" | "DEFECT" | "COA" | "BEFORE" | "AFTER";

export interface QcMediaItem {
  url: string;
  category: QcMediaCategory;
  fileName?: string;
  caption?: string;
  uploadedAt?: string;
}

// Top-level helper to normalize legacy or structured attachments (Rule 11 Module-Level Export Safety)
export function normalizeQcAttachments(
  raw: any,
  defaultMode: "QC" | "NCR" = "QC"
): QcMediaItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item: any): QcMediaItem | null => {
        if (!item) return null;
        if (typeof item === "string") {
          return {
            url: item,
            category: defaultMode === "NCR" ? "BEFORE" : "DEFECT",
            fileName: item.split("/").pop() || "image.jpg",
          };
        }
        if (typeof item === "object" && (item.url || item.fileUrl)) {
          const rawCat = String(item.category || "").toUpperCase();
          let category: QcMediaCategory =
            defaultMode === "NCR" ? "BEFORE" : "DEFECT";
          if (
            rawCat === "SAMPLE" ||
            rawCat === "DEFECT" ||
            rawCat === "COA" ||
            rawCat === "BEFORE" ||
            rawCat === "AFTER"
          ) {
            category = rawCat as QcMediaCategory;
          }
          if (defaultMode === "NCR" && category === "DEFECT") {
            category = "BEFORE";
          }
          return {
            url: item.url || item.fileUrl,
            category,
            fileName: item.fileName || item.name || "attachment",
            caption: item.caption || item.note || "",
            uploadedAt: item.uploadedAt || undefined,
          };
        }
        return null;
      })
      .filter((x): x is QcMediaItem => Boolean(x));
  }

  if (typeof raw === "object") {
    const list: QcMediaItem[] = [];
    const pushArr = (arr: any, cat: QcMediaCategory) => {
      if (!Array.isArray(arr)) return;
      for (const it of arr) {
        if (typeof it === "string") {
          list.push({ url: it, category: cat, fileName: it.split("/").pop() });
        } else if (it?.url) {
          list.push({
            url: it.url,
            category: cat,
            fileName: it.fileName,
            caption: it.caption,
          });
        }
      }
    };
    pushArr(raw.samplePhotos, "SAMPLE");
    pushArr(raw.defectPhotos, defaultMode === "NCR" ? "BEFORE" : "DEFECT");
    pushArr(raw.coaDocuments, "COA");
    pushArr(raw.beforePhotos, "BEFORE");
    pushArr(raw.afterPhotos, "AFTER");
    return list;
  }

  return [];
}

export const normalizeCategorizedAttachments = normalizeQcAttachments;

async function compressImageFile(file: File, maxSizeKB = 1200): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      if (!dataUrl) {
        resolve(file);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        const MAX_DIM = 1400;
        if (width > height && width > MAX_DIM) {
          height *= MAX_DIM / width;
          width = MAX_DIM;
        } else if (height > MAX_DIM) {
          width *= MAX_DIM / height;
          height = MAX_DIM;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0, width, height);
        let quality = 0.88;
        let res = canvas.toDataURL("image/jpeg", quality);
        while (res.length / 1024 > maxSizeKB && quality > 0.2) {
          quality -= 0.1;
          res = canvas.toDataURL("image/jpeg", quality);
        }
        const arr = res.split(",");
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        resolve(
          new File([u8arr], file.name || "qc-photo.jpg", {
            type: "image/jpeg",
          })
        );
      };
      img.onerror = () => resolve(file);
      img.src = dataUrl;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

interface QcCategorizedMediaSectionProps {
  mode?: "QC" | "NCR";
  attachments: any;
  onChange?: (nextItems: QcMediaItem[]) => void;
  readOnly?: boolean;
}

export const QcCategorizedMediaSection: React.FC<QcCategorizedMediaSectionProps> = ({
  mode = "QC",
  attachments,
  onChange,
  readOnly = false,
}) => {
  const items = normalizeQcAttachments(attachments, mode);
  const [uploadingCat, setUploadingCat] = useState<QcMediaCategory | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const categories: Array<{
    key: QcMediaCategory;
    title: string;
    subtitle: string;
    badgeColor: string;
    borderColor: string;
    accept: string;
    icon: React.ReactNode;
  }> =
    mode === "NCR"
      ? [
          {
            key: "BEFORE",
            title: "1. Ảnh Lỗi (Trước Xử Lý)",
            subtitle: "Bằng chứng lỗi phát sinh",
            badgeColor: "bg-red-50 text-red-700 border-red-200",
            borderColor: "border-red-200/80 bg-red-50/20",
            accept: "image/*,.pdf",
            icon: <AlertTriangle className="w-4 h-4 text-red-600" />,
          },
          {
            key: "AFTER",
            title: "2. Ảnh Kết Quả (Sau Xử Lý)",
            subtitle: "Bằng chứng sau khắc phục",
            badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
            borderColor: "border-emerald-200/80 bg-emerald-50/20",
            accept: "image/*,.pdf",
            icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
          },
        ]
      : [
          {
            key: "SAMPLE",
            title: "1. Ảnh Mẫu Kiểm / Mẫu Ký",
            subtitle: "Mẫu chuẩn & tờ đầu chuyền",
            badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
            borderColor: "border-blue-200/80 bg-blue-50/20",
            accept: "image/*",
            icon: <Camera className="w-4 h-4 text-blue-600" />,
          },
          {
            key: "DEFECT",
            title: "2. Ảnh Lỗi Thực Tế (NG)",
            subtitle: "Vị trí lỗi phát hiện (nếu có)",
            badgeColor: "bg-rose-50 text-rose-700 border-rose-200",
            borderColor: "border-rose-200/80 bg-rose-50/20",
            accept: "image/*",
            icon: <AlertTriangle className="w-4 h-4 text-rose-600" />,
          },
          {
            key: "COA",
            title: "3. Hồ Sơ CoA / Test Report",
            subtitle: "Ảnh hoặc PDF chứng nhận",
            badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
            borderColor: "border-emerald-200/80 bg-emerald-50/20",
            accept: "image/*,.pdf",
            icon: <FileCheck2 className="w-4 h-4 text-emerald-600" />,
          },
        ];

  const handleFileUpload = async (
    category: QcMediaCategory,
    fileList: FileList | null
  ) => {
    if (!fileList || fileList.length === 0 || !onChange) return;
    setUploadingCat(category);
    try {
      const uploadedItems: QcMediaItem[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const rawFile = fileList[i];
        const processed = await compressImageFile(rawFile);
        const { fileUrl } = await uploadApi.uploadFile(processed);
        uploadedItems.push({
          url: fileUrl,
          category,
          fileName: rawFile.name,
          caption: "",
          uploadedAt: new Date().toISOString(),
        });
      }
      onChange([...items, ...uploadedItems]);
      toast.success(`Đã tải lên ${uploadedItems.length} tệp đính kèm`);
    } catch (err: any) {
      toast.error("Lỗi tải tệp lên server: " + extractErrorMessage(err));
    } finally {
      setUploadingCat(null);
      if (fileRefs.current[category]) {
        fileRefs.current[category]!.value = "";
      }
    }
  };

  const handleRemoveItem = (globalIdx: number) => {
    if (!onChange) return;
    onChange(items.filter((_, idx) => idx !== globalIdx));
  };

  const handleCaptionChange = (globalIdx: number, caption: string) => {
    if (!onChange) return;
    onChange(
      items.map((it, idx) => (idx === globalIdx ? { ...it, caption } : it))
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-2xs space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
            {mode === "NCR"
              ? "Hồ Sơ Hình Ảnh Bằng Chứng Lỗi & Kết Quả Trước / Sau Xử Lý"
              : "Hồ Sơ Hình Ảnh Mẫu Kiểm, Hình Ảnh Lỗi & Chứng Chỉ CoA"}
          </span>
        </div>
        <Badge variant="outline" className="text-xs font-mono bg-slate-50">
          Tổng đính kèm: {items.length} tệp
        </Badge>
      </div>

      <div
        className={`grid grid-cols-1 ${
          mode === "NCR" ? "md:grid-cols-2" : "md:grid-cols-3"
        } gap-3`}
      >
        {categories.map((cat) => {
          const catItems = items
            .map((item, globalIdx) => ({ item, globalIdx }))
            .filter((x) => x.item.category === cat.key);
          const isUploading = uploadingCat === cat.key;

          return (
            <div
              key={cat.key}
              className={`rounded-lg border p-3 flex flex-col justify-between space-y-2.5 ${cat.borderColor}`}
            >
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {cat.icon}
                    <div>
                      <div className="text-xs font-bold text-slate-900">
                        {cat.title}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {cat.subtitle}
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] font-mono ${cat.badgeColor}`}>
                    {catItems.length} tệp
                  </Badge>
                </div>

                {/* Gallery */}
                {catItems.length === 0 ? (
                  <div className="py-5 text-center text-[11px] text-slate-400 border border-dashed border-slate-200/90 rounded-md bg-white/70">
                    Chưa có tệp đính kèm
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {catItems.map(({ item, globalIdx }) => {
                      const isPdf =
                        item.url?.toLowerCase().endsWith(".pdf") ||
                        item.fileName?.toLowerCase().endsWith(".pdf");
                      return (
                        <div
                          key={`${item.url}-${globalIdx}`}
                          className="group relative rounded-md border border-slate-200 bg-white p-1.5 shadow-2xs space-y-1"
                        >
                          {isPdf ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="flex flex-col items-center justify-center h-24 rounded bg-slate-50 hover:bg-slate-100 text-indigo-700 p-2 text-center"
                            >
                              <FileText className="w-7 h-7 mb-1 text-indigo-600" />
                              <span className="text-[10px] font-semibold line-clamp-2 break-all">
                                {item.fileName || "Hồ sơ CoA.pdf"}
                              </span>
                            </a>
                          ) : (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block relative h-24 rounded overflow-hidden bg-slate-100"
                            >
                              <img
                                src={item.url}
                                alt={item.fileName || cat.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                              <span className="absolute bottom-1 right-1 bg-black/60 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                <ExternalLink className="w-3 h-3" />
                              </span>
                            </a>
                          )}

                          {!readOnly ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={item.caption || ""}
                                onChange={(e) =>
                                  handleCaptionChange(globalIdx, e.target.value)
                                }
                                placeholder="Ghi chú ảnh..."
                                className="h-6 text-[10px] px-1.5"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(globalIdx)}
                                className="p-1 text-slate-400 hover:text-red-600 rounded"
                                title="Xóa tệp"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            item.caption && (
                              <div className="text-[10px] text-slate-600 truncate px-1">
                                {item.caption}
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {!readOnly && (
                <div>
                  <input
                    ref={(el) => {
                      fileRefs.current[cat.key] = el;
                    }}
                    type="file"
                    multiple
                    accept={cat.accept}
                    className="hidden"
                    onChange={(e) => handleFileUpload(cat.key, e.target.files)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => fileRefs.current[cat.key]?.click()}
                    className="w-full h-8 text-xs font-semibold bg-white hover:bg-slate-50"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Đang tải lên...
                      </>
                    ) : (
                      <>
                        <UploadCloud className="w-3.5 h-3.5 mr-1.5 text-indigo-600" />
                        Tải lên {cat.key === "COA" ? "Ảnh / PDF CoA" : "Hình ảnh"}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
